import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type ActivityPanelMode = "default" | "complete";

export interface ActivityPanel {
  activityId: number;
  minimized: boolean;
  initialMode?: ActivityPanelMode;
}

interface ActivityPanelState {
  panels: ActivityPanel[];
  focusedActivityId: number | null;
}

const STORAGE_KEY = "openActivityPanels";
const MAX_PANELS = 3;

const loadState = (): ActivityPanelState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return { panels: parsed, focusedActivityId: parsed[0]?.activityId ?? null };
      }
      return parsed;
    }
  } catch (e) {
    console.error("Could not load activity panels from localStorage:", e);
  }
  return { panels: [], focusedActivityId: null };
};

const saveState = (state: ActivityPanelState) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ panels: state.panels, focusedActivityId: state.focusedActivityId })
    );
  } catch (e) {
    console.error("Could not save activity panels to localStorage:", e);
  }
};

const initialState: ActivityPanelState = loadState();

const activityPanelSlice = createSlice({
  name: "activityPanel",
  initialState,
  reducers: {
    openActivityPanel(state, action: PayloadAction<number | { activityId: number; mode?: ActivityPanelMode }>) {
      const payload = typeof action.payload === "number"
        ? { activityId: action.payload, mode: undefined }
        : action.payload;
      const { activityId, mode } = payload;
      const existing = state.panels.find((p) => p.activityId === activityId);
      if (existing) {
        existing.minimized = false;
        existing.initialMode = mode;
      } else {
        if (state.panels.length >= MAX_PANELS) {
          state.panels.pop();
        }
        state.panels.push({ activityId, minimized: false, initialMode: mode });
      }
      state.focusedActivityId = activityId;
      saveState(state);
    },
    closeActivityPanel(state, action: PayloadAction<number>) {
      state.panels = state.panels.filter((p) => p.activityId !== action.payload);
      if (state.focusedActivityId === action.payload) {
        state.focusedActivityId = state.panels[0]?.activityId ?? null;
      }
      saveState(state);
    },
    toggleActivityMinimize(state, action: PayloadAction<number>) {
      const panel = state.panels.find((p) => p.activityId === action.payload);
      if (panel) {
        panel.minimized = !panel.minimized;
        saveState(state);
      }
    },
    focusActivityPanel(state, action: PayloadAction<number>) {
      if (state.panels.some((p) => p.activityId === action.payload)) {
        state.focusedActivityId = action.payload;
        saveState(state);
      }
    },
    closeAllActivityPanels(state) {
      state.panels = [];
      state.focusedActivityId = null;
      saveState(state);
    },
  },
});

export const {
  openActivityPanel,
  closeActivityPanel,
  toggleActivityMinimize,
  focusActivityPanel,
  closeAllActivityPanels,
} = activityPanelSlice.actions;
export default activityPanelSlice.reducer;
